import { masks } from '@barfacil/barfacil-ui'

import { Product, UpdateProductBody } from '@/api/_schemas/management'
import { FormUpdateProduct } from '@/app/panel/management/price-list/_schemas/update-product'
import { ComboProduct } from '@/app/panel/management/price-list/_schemas/update-product-types'
import { convertToNumber, formatToNumber } from '@/lib/utils'

function useUpdateProductMapper() {
  function mapEntityToForm(data: Product) {
    return {
      tipo: data.tipo,
      nome: data.nome,
      produto_pai: data.produto_pai?.cod_produto,
      produto_pai_nome: data.produto_pai?.nome,
      imagem: data.imagem,
      vlr_custo: masks.currency(Number(data.vlr_custo ?? 0).toFixed(2)),
      tipo_valor: data.tipo_valor,
      situacao: data.situacao,
      consumo: data.consumo,
      permitir_observacao: !!data.permitir_observacao,
      servico: data.servico,
      cod_produto: data.cod_produto,
      cod_externo: data.cod_externo,
      unidade_medida: data.unidade_medida,
      cfop: data.cfop,
      ncm: data.ncm,
      icms_situacao_tributaria: data.icms_situacao_tributaria,
      icms_origem: data.icms_origem,
      icms_aliquota: Number(data.icms_aliquota ?? 0).toFixed(2),
      icms_percentual_reducao_base_calculo: Number(
        data.icms_percentual_reducao_base_calculo ?? 0,
      ).toFixed(2),
      icms_modalidade_base_calculo: data.icms_modalidade_base_calculo,
      pis_situacao_tributaria: '',
      pis_tipo_tributacao: data.pis_tipo_tributacao,
      pis_aliquota_percentual: Number(
        data.pis_aliquota_percentual ?? 0,
      ).toFixed(2),
      cofins_situacao_tributaria: '',
      cofins_tipo_tributacao: data.cofins_tipo_tributacao,
      cofins_aliquota_percentual: Number(
        data.cofins_aliquota_percentual ?? 0,
      ).toFixed(2),
      cest: data.cest,
      tipo_ticket_combo: data.tipo_ticket_combo,
      produto_combo_list: data.produto_combo_list?.map((item) => ({
        cod_produto_combo: item.cod_produto_combo.toString(),
        produto: item.produto.cod_produto.toString(),
        produto_nome: item.produto.nome,
        qtde_produto: item.qtde_produto.toString(),
        vlr_produto: masks.currency(Number(item.vlr_produto ?? 0).toFixed(2)),
      })),
      grupo_tributario: '',
    } as FormUpdateProduct
  }
  function mapFormToPayload(
    data: FormUpdateProduct,
    comboProducts: ComboProduct[],
  ) {
    const isCombo = data.tipo === 3

    function getNullFromfilteredKeys(filter: string) {
      return Object.fromEntries(
        Object.keys(data)
          .filter((key) => key.startsWith(filter))
          .map((key) => [key, null]),
      )
    }

    function getIcmsValues() {
      return isCombo
        ? getNullFromfilteredKeys('icms_')
        : {
            icms_situacao_tributaria: data.icms_situacao_tributaria,
            icms_origem: data.icms_origem,
            icms_aliquota: ['00', '60'].includes(
              data.icms_situacao_tributaria ?? '',
            )
              ? convertToNumber(data.icms_aliquota)
              : null,
            icms_percentual_reducao_base_calculo:
              data.icms_situacao_tributaria === '60'
                ? convertToNumber(data.icms_percentual_reducao_base_calculo)
                : null,
            icms_modalidade_base_calculo:
              data.icms_situacao_tributaria === '00'
                ? data.icms_modalidade_base_calculo
                : null,
          }
    }

    function getPisValues() {
      return isCombo
        ? getNullFromfilteredKeys('pis_')
        : {
            pis_situacao_tributaria: data.pis_situacao_tributaria,
            pis_tipo_tributacao: ['01', '02'].includes(
              data.pis_situacao_tributaria ?? '',
            )
              ? data.pis_tipo_tributacao
              : null,
            pis_aliquota_percentual: ['01', '02'].includes(
              data.pis_situacao_tributaria ?? '',
            )
              ? convertToNumber(data.pis_aliquota_percentual)
              : null,
          }
    }

    function getCofinsValues() {
      return isCombo
        ? getNullFromfilteredKeys('cofins_')
        : {
            cofins_situacao_tributaria: data.cofins_situacao_tributaria,
            cofins_tipo_tributacao: ['01', '02'].includes(
              data.cofins_situacao_tributaria ?? '',
            )
              ? data.cofins_tipo_tributacao
              : null,
            cofins_aliquota_percentual: ['01', '02'].includes(
              data.cofins_situacao_tributaria ?? '',
            )
              ? convertToNumber(data.cofins_aliquota_percentual)
              : null,
          }
    }

    return {
      tipo: data.tipo,
      nome: data.nome,
      produto_pai: data.produto_pai,
      imagem: data.imagem ?? null,
      vlr_custo: parseFloat(formatToNumber(data.vlr_custo)),
      tipo_valor: data.tipo_valor,
      situacao: data.situacao,
      consumo: data.consumo,
      permitir_observacao: data.permitir_observacao,
      servico: data.servico,
      cod_produto: data.cod_produto,
      cod_externo: isCombo ? null : data.cod_externo,
      unidade_medida: isCombo ? null : data.unidade_medida,
      grupo_tributario: data.grupo_tributario,
      cfop: isCombo ? null : data.cfop,
      ncm: isCombo ? null : data.ncm,
      ...getIcmsValues(),
      ...getPisValues(),
      ...getCofinsValues(),
      cest:
        isCombo ||
        !['60', '500', '900'].includes(data.icms_situacao_tributaria ?? '')
          ? data.cest
          : null,
      tipo_ticket_combo: isCombo ? data.tipo_ticket_combo : null,
      produto_combo_list: isCombo
        ? comboProducts.map((item) => ({
            cod_produto_combo: item.cod_produto_combo
              ? item.cod_produto_combo
              : undefined,
            produto: item.produto.cod_produto
              ? item.produto.cod_produto
              : undefined,
            produto_nome: item.produto.nome || undefined,
            qtde_produto: item.qtde_produto,
            vlr_produto: item.vlr_produto,
          }))
        : [],
    } as UpdateProductBody
  }

  return { mapEntityToForm, mapFormToPayload }
}

export { useUpdateProductMapper }
